import { Link } from "react-router-dom";
import {
  PhoneCall,
  ShieldCheck,
  Clock,
  Lightning,
  Star,
  MapPin,
  Wrench,
  ArrowRight,
  ChatCircleText,
  CheckCircle,
} from "@phosphor-icons/react";
import CallButton from "../components/CallButton";
import TrustBar from "../components/TrustBar";
import FinalCTABanner from "../components/FinalCTABanner";
import Testimonials from "../components/Testimonials";
import ServiceCard from "../components/ServiceCard";
import StaticMap from "../components/StaticMap";
import SEO from "../components/SEO";
import { BUSINESS, IMAGES } from "../data/constants";
import { SERVICES } from "../data/services";
import { CITIES } from "../data/cities";

const WHY_US = [
  {
    icon: Lightning,
    title: "Fast Response",
    desc: "Technicians across the GTA, ready to move the moment you call.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed & Insured",
    desc: "Fully certified, background-checked technicians on every call.",
  },
  {
    icon: ChatCircleText,
    title: "Straight Answers",
    desc: "A real tech on the phone. Clear diagnosis. No runaround, no upsells.",
  },
  {
    icon: Star,
    title: "5-Star Service",
    desc: "Hundreds of happy GTA homeowners. 4.9/5 average rating.",
  },
];

export default function Home() {
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": `${BUSINESS.url}/#business`,
        name: BUSINESS.name,
        url: BUSINESS.url,
        telephone: BUSINESS.phone,
        image: IMAGES.heroTechnician,
        address: {
          "@type": "PostalAddress",
          addressLocality: "Toronto",
          addressRegion: "ON",
          addressCountry: "CA",
        },
        areaServed: CITIES.map((c) => ({
          "@type": "City",
          name: c.name,
        })),
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
              "Sunday",
            ],
            opens: "00:00",
            closes: "23:59",
          },
        ],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: String(BUSINESS.reviewCount),
          bestRating: "5",
          worstRating: "1",
        },
      },
    ],
  };

  return (
    <>
      <SEO
        title="Garage Door Repair GTA | 24/7 Emergency Service"
        description="Broken garage door or locked out in the GTA? We dispatch a licensed tech in 60 min. Call now for 24/7 emergency service - fast, fair, reliable."
        canonical="/"
        ogImage={IMAGES.heroTechnician}
        schema={schema}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#1B2A4A] text-white" data-testid="hero-section">
        <img
          src={IMAGES.heroTechnician}
          alt="GTA Garage Doors licensed technician arriving at a residential home in Toronto"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1B2A4A] via-[#1B2A4A]/85 to-[#1B2A4A]/40" aria-hidden />
        <div className="absolute inset-0 grain" aria-hidden />

        <div className="relative mx-auto max-w-7xl px-4 md:px-6 py-16 md:py-28 lg:py-36">
          <div className="max-w-3xl animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-sm bg-[#FF6B00] text-white px-3 py-1.5 text-xs font-bold font-display uppercase tracking-widest">
              <Lightning size={14} weight="fill" />
              24/7 Emergency Dispatch
            </div>
            <h1
              data-testid="hero-h1"
              className="mt-5 font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight leading-[1.05]"
            >
              Garage Door Broken? <br />
              Locked Out?{" "}
              <span className="text-[#FF6B00]">We're On Our Way.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
              24/7 Emergency Service Across the GTA — Most Calls Answered in
              Under 60 Minutes.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 items-start sm:items-center">
              <CallButton
                size="lg"
                testId="hero-call-now-button"
                label={`CALL NOW  ${BUSINESS.phone}`}
              />
              <Link
                to="/services"
                data-testid="hero-services-link"
                className="inline-flex items-center gap-2 px-6 py-4 font-bold font-display text-white border-2 border-white/30 rounded-sm hover:bg-white hover:text-[#1B2A4A] transition-all"
              >
                See All Services
                <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <TrustBar variant="dark" />

      {/* EMOTIONAL HOOK */}
      <section className="section-pad bg-white" data-testid="emotional-hook">
        <div className="container-tight grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow">Why call us now</div>
            <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1B2A4A]">
              Every Minute Locked Out Is a Minute Too Long.
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#4B5563] leading-relaxed">
              Whether your garage door spring snapped at 6am, your door was
              kicked in overnight, or your lock is jammed in -20 degree
              weather — we don't make you wait. GTA Garage Doors dispatches a
              licensed technician to your door fast, so you can get back to
              your life.
            </p>
            <div className="mt-8">
              <CallButton
                size="lg"
                testId="emotional-hook-call-button"
                label={`CALL NOW  ${BUSINESS.phone}`}
              />
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img
                src={IMAGES.locksmith}
                alt="GTA locksmith installing a new deadbolt on a home front door"
                className="w-full h-80 md:h-96 object-cover rounded-sm border border-[#E2E8F0]"
                loading="lazy"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#FF6B00] text-white p-4 md:p-6 rounded-sm shadow-xl max-w-[220px]">
                <div className="font-display font-black text-3xl md:text-4xl leading-none">
                  45<span className="text-lg">min</span>
                </div>
                <div className="text-xs font-bold uppercase tracking-widest mt-1">
                  Average GTA Response
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="section-pad bg-[#F8FAFC]" data-testid="services-grid-section">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="eyebrow">What we fix</div>
              <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1B2A4A] max-w-2xl">
                One call. Every residential emergency we show up for.
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="view-all-services-link"
              className="inline-flex items-center gap-2 font-bold font-display text-[#FF6B00] uppercase tracking-wider text-sm hover:underline"
            >
              View all services <ArrowRight size={16} weight="bold" />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {SERVICES.slice(0, 6).map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section-pad bg-white" data-testid="why-choose-us">
        <div className="container-wide">
          <div className="max-w-3xl">
            <div className="eyebrow">Why GTA Garage Doors</div>
            <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1B2A4A]">
              Built for emergencies. Built for homeowners.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
            {WHY_US.map((item) => (
              <div
                key={item.title}
                data-testid={`why-us-${item.title.toLowerCase().replace(/ /g, "-")}`}
                className="flex flex-col"
              >
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-sm bg-[#1B2A4A] text-white">
                  <item.icon size={28} weight="fill" className="text-[#FF6B00]" />
                </div>
                <div className="mt-5 font-display font-black text-xl md:text-2xl text-[#1B2A4A] tracking-tight">
                  {item.title}
                </div>
                <p className="mt-2 text-sm md:text-base text-[#4B5563] leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      {/* SERVICE AREAS */}
      <section className="section-pad bg-white" data-testid="service-areas">
        <div className="container-wide">
          <div className="text-center max-w-2xl mx-auto">
            <div className="eyebrow">Serving the Entire GTA</div>
            <h2 className="mt-3 font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1B2A4A]">
              One team. Twelve cities. Always moving.
            </h2>
            <p className="mt-4 text-base md:text-lg text-[#4B5563]">
              Our technicians cover every major GTA neighbourhood, 24 hours a
              day. Click your city to see local service details.
            </p>
          </div>

          <div className="mt-10">
            <StaticMap city="Greater Toronto Area" testId="home-map" />
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
            {CITIES.map((c) => (
              <Link
                key={c.slug}
                to={`/areas/${c.slug}`}
                data-testid={`city-tile-${c.slug}`}
                className="group flex items-center justify-between gap-2 rounded-sm border border-[#E2E8F0] bg-white px-4 py-3 hover:border-[#FF6B00] hover:-translate-y-0.5 transition-all"
              >
                <span className="inline-flex items-center gap-2 font-display font-bold text-[#1B2A4A]">
                  <MapPin size={16} weight="fill" className="text-[#FF6B00]" />
                  {c.name}
                </span>
                <ArrowRight size={14} weight="bold" className="text-[#1B2A4A]/40 group-hover:text-[#FF6B00] transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <FinalCTABanner />
    </>
  );
}
